import React, { useEffect, useRef, useState } from "react";
import { Container, Spinner, Table, Button, Form } from "react-bootstrap";
import axios from "../../../api/axios";
import useAuth from "../../../hooks/useAuth";

const columns = [
  { field: "fecha", label: "Fecha", required: true },
  { field: "litros", label: "Litros", required: true },
  { field: "grasa", label: "% Grasa" },
  { field: "proteina", label: "% Proteina" },
  { field: "temperatura", label: "Temperatura" },
  { field: "sng", label: "Sng" },
  { field: "crioscopia", label: "Crioscopia" },
  { field: "porc_agua", label: "% Agua" },
  { field: "inhibidores", label: "Inhibidores" },
  { field: "urea", label: "Urea" },
  { field: "ufc", label: "Ufc" },
  { field: "cs", label: "cs" },
  { field: "nombre_tambo", label: "Nombre de tambo", defaultVisible: false },
  { field: "nro_tambo", label: "Nro de tambo", defaultVisible: false },
  { field: "nro_muestra", label: "Muestra" },
];
const COLUMN_PREFERENCES_URL = "/users/me/preferences/dairies";
const defaultVisibleColumns = columns
  .filter((column) => column.defaultVisible !== false)
  .map((column) => column.field);
const requiredColumnFields = columns
  .filter((column) => column.required)
  .map((column) => column.field);
const validColumnFields = new Set(columns.map((column) => column.field));

const TableCustom = ({ axiosData, refresh,loadingData }) => {
  const { auth } = useAuth();
  const refreshRef = useRef(refresh);
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState("");
  const [visibleColumns, setVisibleColumns] = useState(defaultVisibleColumns);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  useEffect(() => {
    let isMounted = true;

    refreshRef.current();
    axios.get(COLUMN_PREFERENCES_URL, {
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + auth?.accessToken,
      },
    }).then((response) => {
      const savedColumns = response?.data?.visibleColumns;
      if (!isMounted) return;
      setPreferencesLoaded(true);
      if (!Array.isArray(savedColumns)) return;

      const validSavedColumns = savedColumns.filter((field) => (
        validColumnFields.has(field) && !requiredColumnFields.includes(field)
      ));
      setVisibleColumns([...requiredColumnFields, ...validSavedColumns]);
    }).catch((error) => {
      if (isMounted) {
        setPreferencesLoaded(true);
      }
      if (error?.response?.status !== 404) {
        console.error(error);
      }
    });

    return () => {
      isMounted = false;
    };
  }, [auth?.accessToken]);

  useEffect(() => {
    if (!preferencesLoaded) return;

    axios.put(COLUMN_PREFERENCES_URL, { visibleColumns }, {
      headers: {
        "Content-Type": "application/json",
        Authorization: "Bearer " + auth?.accessToken,
      },
    }).catch((error) => console.error(error));
  }, [preferencesLoaded, visibleColumns, auth?.accessToken]);

  useEffect(() => {
    setData(axiosData);
  }, [axiosData]);

  const filteredData = data.filter((row) =>
    Object.values(row).some((value) => String(value ?? "").toLowerCase().includes(filter.toLowerCase()))
  );
  const totalLitros = filteredData.reduce((total, row) => total + (Number(row.litros) || 0), 0);

  const formatValue = (field, value) => {
    if (field === "fecha" && value) {
      const [year, month, day] = String(value).slice(0, 10).split("-");
      return `${day}/${month}/${year}`;
    }
    return value ?? "";
  };

  const exportCsv = () => {
    const header = visibleColumns.map((field) => columns.find((column) => column.field === field).label);
    const rows = filteredData.map((row) => visibleColumns.map((field) => formatValue(field, row[field])));
    const csv = [header, ...rows]
      .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const link = document.createElement("a");
    link.href = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" }));
    link.download = "Entregas.csv";
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const toggleColumn = (field) => {
    setVisibleColumns((current) => current.includes(field)
      ? current.filter((currentField) => currentField !== field)
      : [...current, field]);
  };

  return (
    <div>
      {loadingData ? (
        <Container className="text-center d-flex flex-column align-items-center my-5">
          <Spinner
            animation="border"
            role="status"
            style={{ width: "8rem", height: "8rem" }}
          >
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="text-center my-3 fw-bold fs-3 text-success">
            Cargando datos
          </p>
        </Container>
      ) : (
        <>
          <div className="d-flex gap-2 flex-wrap mt-3 mb-3">
            <Form.Control
              type="search"
              placeholder="Buscar en la tabla"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
            />
          </div>
          <div className="d-flex flex-wrap gap-3 mb-3">
            {columns.map((column) => (
              <Form.Check
                key={column.field}
                type="checkbox"
                label={column.label}
                checked={visibleColumns.includes(column.field)}
                disabled={column.required}
                onChange={() => toggleColumn(column.field)}
              />
            ))}
          </div>
          <Table striped hover responsive size="sm">
            <thead className="table-green">
              <tr>{columns.filter((column) => visibleColumns.includes(column.field)).map((column) => (
                <th key={column.field}>{column.label}</th>
              ))}</tr>
            </thead>
            <tbody>
              {filteredData.map((row, index) => (
                <tr key={row._id || index}>
                  {columns.filter((column) => visibleColumns.includes(column.field)).map((column) => (
                    <td key={column.field}>{formatValue(column.field, row[column.field])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
          {filteredData.length === 0 && <p className="text-center">No se encontraron valores para el usuario actual</p>}
          <div className="d-flex justify-content-start mb-3">
            <Button variant="success" onClick={exportCsv}>Exportar CSV</Button>
          </div>
          {filteredData.length > 0 && (
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex gap-3">
                <span>{filteredData.length} registros</span>
                <span>Total: {totalLitros} litros</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TableCustom;
